import React, { useState, useEffect } from 'react';
import { FiUpload, FiImage, FiX, FiCheck, FiTrash2 } from 'react-icons/fi';
import { API_UPLOAD_URL, UPLOAD_CONFIG, normalizeMediaUrl } from '../../config/apiConfig';
import '../ScrollStyles.css';

/**
 * Componente reutilizável de seleção de imagem
 * Permite upload de nova imagem ou seleção de imagem já existente
 */
export default function ImageSelector({ 
  currentImage, 
  onImageSelect, 
  title = "Selecionar Imagem",
  showGallery = true,
  accept = "image/*",
  fileType = "imagem",
  cardTitle = "",
  cardHint = ""
}) {
  const [mode, setMode] = useState('upload'); // 'upload' | 'gallery'
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [galleryImages, setGalleryImages] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [selectedFromGallery, setSelectedFromGallery] = useState(null);

  // Carregar galeria de imagens - sempre que montar o componente
  useEffect(() => {
    if (showGallery) {
      loadGallery();
    }
  }, [showGallery]);

  async function loadGallery() {
    setLoadingGallery(true);
    setError('');
    try {
      // Endpoint para listar imagens
      const apiUrl = API_UPLOAD_URL.replace('/upload.php', '/list-images.php');
      const response = await fetch(apiUrl);
      const data = await response.json();

      if (data.success && data.data) {
        // Filtrar apenas imagens (não incluir MP3 nem imagens de packs)
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
        const imagesOnly = data.data.filter(file => {
          const ext = file.filename.split('.').pop().toLowerCase();
          const isImage = imageExtensions.includes(ext);
          
          // EXCLUIR imagens de packs (galeria separada!)
          const path = file.path || file.filename;
          const isPack = path.includes('packs/') || file.filename.includes('pack_');
          
          return isImage && !isPack; // ← Apenas imagens QUE NÃO SEJAM de packs
        });
        setGalleryImages(imagesOnly);
      } else {
        setError('Erro ao carregar galeria.');
      }
    } catch (err) {
      console.error('Erro ao carregar galeria:', err);
      setError('Erro de conexão ao carregar galeria.');
    } finally {
      setLoadingGallery(false);
    }
  }

  async function handleImageUpload(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Validação de tipo
    if (!UPLOAD_CONFIG.allowedTypes.includes(file.type)) {
      setError('Tipo de arquivo não permitido. Use apenas JPG, PNG, GIF ou WEBP.');
      return;
    }

    // Validação de tamanho
    if (file.size > UPLOAD_CONFIG.maxFileSize) {
      setError('Arquivo muito grande. Máximo: 10MB.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await fetch(API_UPLOAD_URL, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setUploadProgress(100);
        onImageSelect(data.data.url);
        // Atualizar galeria em tempo real
        if (showGallery) {
          loadGallery();
        }
      } else {
        setError(data.message || 'Erro ao fazer upload da imagem.');
      }
    } catch (err) {
      console.error('Erro no upload:', err);
      setError('Erro de conexão com o servidor de upload.');
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  }

  function handleSelectFromGallery(imageUrl) {
    setSelectedFromGallery(normalizeMediaUrl(imageUrl));
    onImageSelect(normalizeMediaUrl(imageUrl));
  }

  async function handleDeleteFile(filename, fileUrl) {
    // Confirmação
    if (!window.confirm(`Tem certeza que deseja excluir "${filename}"?`)) {
      return;
    }

    try {
      const deleteUrl = API_UPLOAD_URL.replace('/upload.php', '/delete.php');
      const response = await fetch(deleteUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filename }),
      });

      const data = await response.json();

      if (data.success) {
        // Se a imagem deletada era a selecionada, limpar seleção
        if (currentImage === fileUrl) {
          onImageSelect('');
        }
        // Atualizar galeria
        loadGallery();
      } else {
        setError(data.message || 'Erro ao excluir arquivo.');
      }
    } catch (err) {
      console.error('Erro ao excluir:', err);
      setError('Erro ao excluir arquivo.');
    }
  }

  return (
    <div style={styles.container}>
      {/* Header do Card */}
      {cardTitle && (
        <div style={styles.cardHeader}>
          <div style={styles.cardHeaderLeft}>
            <div style={styles.cardTitle}>{cardTitle}</div>
            <div style={styles.cardHint}>{cardHint}</div>
          </div>
          <div style={styles.cardHeaderRight}>
            <input
              type="file"
              id={`upload-${title}`}
              accept={accept}
              onChange={handleImageUpload}
              style={styles.fileInput}
              disabled={uploading}
            />
            <label htmlFor={`upload-${title}`} style={styles.uploadButtonCard}>
              <FiUpload size={14} />
              {uploading ? 'Enviando...' : `Upload ${fileType}`}
            </label>
          </div>
        </div>
      )}

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {/* Barra de progresso (se estiver fazendo upload) */}
      {uploading && (
        <div style={styles.progressBar}>
          <div style={{...styles.progressFill, width: `${uploadProgress}%`}}></div>
        </div>
      )}

      {/* Galeria - Sempre visível */}
      {showGallery && (
        <div style={styles.galleryMode}>
          <div style={styles.galleryHeader}>
            <FiImage size={14} style={{marginRight: 6}} /> Galeria ({galleryImages.length})
          </div>
          
          {loadingGallery ? (
            <div style={styles.loading}>Carregando galeria...</div>
          ) : galleryImages.length === 0 ? (
            <div style={styles.empty}>
              <FiImage size={32} style={{ opacity: 0.3 }} />
              <p>Nenhuma imagem na galeria ainda.</p>
            </div>
          ) : (
            <div style={styles.galleryContainer} className="scroll-container">
              <div style={styles.gallery}>
                {galleryImages.map((img, index) => (
                  <div
                    key={index}
                    style={{
                      ...styles.galleryItem,
                      ...(normalizeMediaUrl(currentImage) === normalizeMediaUrl(img.url) ? styles.galleryItemSelected : {})
                    }}
                    onClick={() => handleSelectFromGallery(img.url)}
                  >
                    <img src={normalizeMediaUrl(img.url)} alt={img.filename} style={styles.galleryImg} />
                    {currentImage === img.url && (
                      <div style={styles.selectedBadge}>
                        <FiCheck size={16} />
                      </div>
                    )}
                    {/* Botão de excluir - aparece no hover */}
                    <button
                      className="delete-btn"
                      style={styles.deleteButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Usar path se disponível, senão filename
                        const filePath = img.path || img.filename;
                        handleDeleteFile(filePath, img.url);
                      }}
                      title="Excluir imagem"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    paddingBottom: 12,
    borderBottom: '2px solid #e8e9ef',
  },
  cardHeaderLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#333',
  },
  cardHint: {
    fontSize: 12,
    color: '#666',
  },
  cardHeaderRight: {
    display: 'flex',
    alignItems: 'center',
  },
  uploadButtonCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    background: '#1f6feb',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    userSelect: 'none',
  },
  error: {
    padding: 12,
    background: '#ffe8e6',
    border: '1px solid #ffcbc4',
    borderRadius: 10,
    color: '#9a3c2f',
    fontSize: 14,
    fontWeight: 600,
  },
  fileInput: {
    display: 'none',
  },
  progressBar: {
    width: '100%',
    height: 6,
    background: '#e8e9ef',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    transition: 'width 0.3s ease',
  },
  galleryMode: {
    minHeight: 200,
    marginTop: 16,
  },
  galleryHeader: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    fontWeight: 600,
    color: '#444',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid #e8e9ef',
  },
  loading: {
    textAlign: 'center',
    padding: 40,
    color: '#999',
    fontSize: 14,
  },
  empty: {
    textAlign: 'center',
    padding: 40,
    color: '#999',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },
  galleryContainer: {
    maxHeight: 400,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: 8,
    // Scroll customizado
    scrollbarWidth: 'thin',
    scrollbarColor: '#1f6feb #f0f0f0',
  },
  gallery: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 12,
  },
  galleryItem: {
    position: 'relative',
    border: '2px solid #e8e9ef',
    borderRadius: 10,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: '#fff',
  },
  galleryItemSelected: {
    borderColor: '#1f6feb',
    boxShadow: '0 0 0 3px rgba(31, 111, 235, 0.2)',
  },
  galleryImg: {
    width: '100%',
    height: 140,
    objectFit: 'cover',
    display: 'block',
  },
  selectedBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    background: '#1f6feb',
    color: '#fff',
    borderRadius: '50%',
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  deleteButton: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    background: '#dc3545',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    opacity: 0,
    transition: 'opacity 0.2s, transform 0.2s',
    zIndex: 3,
  },
};
